import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DndContext, PointerSensor, closestCenter, useSensor, useSensors } from "@dnd-kit/core";
import { toast } from "sonner";
import {
    ARMOR_LAYOUT,
    buildInitialPool,
    getCatalogEntry,
    snapQuantityForEntry,
} from "@/data/vipSimulatorCatalog";
import {
    BENEFIT_DEFINITIONS,
    PREMIUM_MIN_BUDGET,
    applyBudgetUnlocks,
    buildBenefitsSummaryList,
    computeBenefitsExtraCost,
    createInitialBenefitState,
    minTierForAmount,
} from "@/data/vipBenefitsConfig";
import {
    emitSimulatorToast,
    formatBRL,
    formatQty,
    formatUnitBRL,
    lineSubtotal,
    unitPriceForKey,
} from "./vipSimulatorUtils";
import {
    applyQuickEquipPreset,
    cloneSim,
    findKitPlacementByCatalogId,
    findLocation,
    forEachKitPlacement,
    getPlacementAt,
    getPlacementBlockReason,
    moveWithinKit,
    parseDropTarget,
    planAddFromPool,
    removeFromKit,
    equipAttachmentFromPool,
    unequipAttachmentFromWeapon,
} from "./vipSimulatorMoves";
import AvailableItems from "./AvailableItems";
import ArmorSlots from "./ArmorSlots";
import QuickEquipBar from "./QuickEquipBar";
import BenefitsPanel from "./BenefitsPanel";
import InventoryGrid from "./InventoryGrid";
import KitItemCard from "./KitItemCard";
import WeaponSlotCard from "./WeaponSlotCard";
import WeaponCustomizeModal from "./WeaponCustomizeModal";
import VipDiscordTicketButton from "./VipDiscordTicketButton";
import { downloadVipInvoice } from "./vipInvoiceExport";
import { buildPlayerIdentityForInvoice } from "@/lib/playerProfile";
import { saveVipSimulatorRequest } from "@/lib/vipSimulatorPersist";
import { loadVipSimulatorDraft, saveVipSimulatorDraft } from "@/lib/vipSimulatorDraftStorage";
import { usePlayerProfile } from "@/hooks/usePlayerProfile";
import zrsLogo from "@/brand/zrsLogo";
import LanguageSwitch from "@/components/LanguageSwitch";
import { getLang, translate, useI18n } from "@/i18n/I18nProvider";
import { itemName } from "@/i18n/items";
import "./VipSimulator.css";

const INV_COLS = 6;
const INV_SLOTS = INV_COLS * 4;
const HOT_SLOTS = 6;
/** Valor mínimo do kit para descarregar fatura e pedir VIP personalizado. */
const MIN_VIP_INVOICE_TOTAL = 20;

function computeKitTotal(sim) {
    let total = 0;
    forEachKitPlacement(sim, (p) => {
        const e = getCatalogEntry(p.catalogId);
        if (!e) return;
        const u = unitPriceForKey(e.priceKey);
        const q = e.quantityEnabled ? p.quantity : 1;
        total += lineSubtotal(u, q);
    });
    return Math.round(total * 10000) / 10000;
}

/**
 * Linhas da fatura no idioma `lang` (nomes de itens traduzidos; o catálogo usa nomes PT).
 * @param {object} sim
 * @param {"pt"|"en"} lang
 */
function buildInvoiceItems(sim, lang) {
    const rows = [];
    forEachKitPlacement(sim, (p, zone, extra) => {
        const e = getCatalogEntry(p.catalogId);
        if (!e) return;
        const unitPrice = unitPriceForKey(e.priceKey);
        const quantity = e.quantityEnabled ? p.quantity : 1;
        const suffix = extra.attachment ? "-att" : "";
        const name = itemName(e.name, lang);
        rows.push({
            key: `${zone}-${extra.index ?? extra.accept ?? 0}-${p.instanceId}${suffix}`,
            name: extra.attachment ? translate("simulator.summary.attachmentSuffix", { name }, lang) : name,
            quantity,
            unitPrice,
            subtotal: lineSubtotal(unitPrice, quantity),
        });
    });
    return rows;
}

function initialArmor() {
    return ARMOR_LAYOUT.reduce((acc, r) => {
        acc[r.accept] = null;
        return acc;
    }, {});
}

function createEmptySim() {
    return {
        pool: buildInitialPool(),
        inv: Array(INV_SLOTS).fill(null),
        hotbar: Array(HOT_SLOTS).fill(null),
        armor: initialArmor(),
    };
}

function hydrateSimFromDraft(draftSim) {
    if (!draftSim || typeof draftSim !== "object") return createEmptySim();
    const base = createEmptySim();
    return {
        pool: draftSim.pool && typeof draftSim.pool === "object" ? draftSim.pool : base.pool,
        inv: Array.isArray(draftSim.inv) && draftSim.inv.length === INV_SLOTS ? draftSim.inv : base.inv,
        hotbar:
            Array.isArray(draftSim.hotbar) && draftSim.hotbar.length === HOT_SLOTS
                ? draftSim.hotbar
                : base.hotbar,
        armor:
            draftSim.armor && typeof draftSim.armor === "object"
                ? { ...base.armor, ...draftSim.armor }
                : base.armor,
    };
}

/**
 * @param {{ user: import('@supabase/supabase-js').User }} props
 */
export default function VipSimulator({ user }) {
    const { t, lang } = useI18n();
    const userId = user?.id ?? null;
    const { profile } = usePlayerProfile(user);
    const savedDraft = useMemo(
        () => (userId ? loadVipSimulatorDraft(userId) : null),
        [userId],
    );

    const [sim, setSim] = useState(() => hydrateSimFromDraft(savedDraft?.sim));
    const [name, setName] = useState(savedDraft?.name ?? "");
    const [budget, setBudget] = useState(
        typeof savedDraft?.budget === "number" ? savedDraft.budget : 20,
    );
    const [benefitState, setBenefitState] = useState(() =>
        savedDraft?.benefitState ? { ...createInitialBenefitState(), ...savedDraft.benefitState } : createInitialBenefitState(),
    );
    const [tab, setTab] = useState(savedDraft?.tab === "beneficios" ? "beneficios" : "kit");
    const [customizeWeaponId, setCustomizeWeaponId] = useState(null);
    const [invoiceSent, setInvoiceSent] = useState(false);

    const lockedPremium = budget < PREMIUM_MIN_BUDGET;

    useEffect(() => {
        if (!userId) return undefined;
        const timer = window.setTimeout(() => {
            saveVipSimulatorDraft(userId, { sim, name, budget, benefitState, tab });
        }, 400);
        return () => window.clearTimeout(timer);
    }, [userId, sim, name, budget, benefitState, tab]);

    const handleBudget = useCallback((b) => {
        setBudget(b);
        const lockedPremium = b < PREMIUM_MIN_BUDGET;
        setBenefitState((prev) => applyBudgetUnlocks(prev, b, lockedPremium));
    }, []);

    const onBenefitPatch = useCallback((id, patch) => {
        setBenefitState((prev) => ({
            ...prev,
            [id]: { ...prev[id], ...patch },
        }));
    }, []);

    const onQuantityChange = useCallback((instanceId, qty) => {
        setSim((prev) => {
            const loc = findLocation(prev, instanceId);
            if (!loc) return prev;
            const cur = getPlacementAt(prev, loc);
            if (!cur) return prev;
            const entry = getCatalogEntry(cur.catalogId);
            if (!entry || !entry.quantityEnabled) return prev;
            const q = snapQuantityForEntry(entry, qty);
            const updated = { ...cur, quantity: q };

            if (loc.kind === "pool") {
                return {
                    ...prev,
                    pool: {
                        ...prev.pool,
                        [loc.category]: prev.pool[loc.category].map((x, i) =>
                            i === loc.index ? updated : x,
                        ),
                    },
                };
            }

            const next = cloneSim(prev);
            if (loc.kind === "inv") next.inv[loc.index] = updated;
            else if (loc.kind === "hotbar") next.hotbar[loc.index] = updated;
            else if (loc.kind === "armor") next.armor[loc.accept] = updated;
            return next;
        });
    }, []);

    const onRemoveFromKit = useCallback((instanceId) => {
        setCustomizeWeaponId((openId) => (openId === instanceId ? null : openId));
        setSim((prev) => removeFromKit(prev, instanceId) || prev);
    }, []);

    const onQuickEquip = useCallback((presetId) => {
        setSim((prev) => {
            const result = applyQuickEquipPreset(prev, presetId);
            if (!result) {
                toast.error(translate("simulator.toast.presetNotFound"));
                return prev;
            }
            if (result.missing.length > 0) {
                toast.warning(
                    translate("simulator.toast.presetMissing", {
                        items: result.missing.map((m) => itemName(m, getLang())).join(", "),
                    }),
                );
            } else {
                toast.success(translate("simulator.toast.presetApplied"));
            }
            return result.sim;
        });
    }, []);

    const mergeConfirmPassRef = useRef(null);

    const onAddFromPool = useCallback((instanceId) => {
        let feedback = null;

        setSim((prev) => {
            const preview = planAddFromPool(prev, instanceId);

            if (preview.needsMergeConfirm) {
                const loc = findLocation(prev, instanceId);
                const template = loc ? getPlacementAt(prev, loc) : null;
                const entry = template ? getCatalogEntry(template.catalogId) : null;
                const existing = entry
                    ? findKitPlacementByCatalogId(prev, template.catalogId)
                    : null;
                if (!entry || !existing) return prev;

                const alreadyConfirmed = mergeConfirmPassRef.current === instanceId;
                if (!alreadyConfirmed) {
                    const cur = getPlacementAt(prev, existing);
                    const newQty = snapQuantityForEntry(entry, cur.quantity + template.quantity);
                    const ok = window.confirm(
                        translate("simulator.confirm.merge", {
                            name: itemName(entry.name, getLang()),
                            qty: formatQty(cur.quantity),
                            newQty: formatQty(newQty),
                        }),
                    );
                    if (!ok) return prev;
                    mergeConfirmPassRef.current = instanceId;
                }

                const merged = planAddFromPool(prev, instanceId, { mergeConfirmed: true });
                feedback = merged.feedback;
                return merged.sim;
            }

            feedback = preview.feedback;
            return preview.sim;
        });

        emitSimulatorToast(feedback);
        mergeConfirmPassRef.current = null;
    }, []);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    );

    const handleDragEnd = useCallback((event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;

        const activeData = active.data.current;
        if (!activeData || activeData.type !== "placement") return;

        const instanceId = activeData.placement.instanceId;

        setSim((prev) => {
            const from = findLocation(prev, instanceId);
            if (!from || from.kind === "pool") return prev;

            const target = parseDropTarget(over, prev);
            if (!target || target.type === "pool") return prev;

            const entry = getCatalogEntry(activeData.placement.catalogId);
            if (target.type === "weapon-attach" && entry?.cat !== "attachment") {
                toast.error(translate("simulator.toast.onlyAttachments"));
                return prev;
            }

            const blockReason = getPlacementBlockReason(prev, target, entry);
            const next = moveWithinKit(prev, instanceId, target);
            if (!next) {
                if (blockReason) toast.error(blockReason);
                else if (target.type === "weapon-attach") {
                    toast.error(translate("simulator.toast.attachIncompatible"));
                } else {
                    toast.error(translate("simulator.toast.cannotMove"));
                }
            }
            return next || prev;
        });
    }, []);

    const onOpenWeaponCustomize = useCallback((instanceId) => {
        setCustomizeWeaponId(instanceId);
    }, []);

    const onCloseWeaponCustomize = useCallback(() => {
        setCustomizeWeaponId(null);
    }, []);

    const onEquipAttachmentFromPool = useCallback((attachmentInstanceId) => {
        if (!customizeWeaponId) return;
        setSim((prev) => {
            const next = equipAttachmentFromPool(prev, customizeWeaponId, attachmentInstanceId);
            if (!next) {
                toast.error(translate("simulator.toast.cannotEquip"));
                return prev;
            }
            const loc = findLocation(prev, attachmentInstanceId);
            const p = loc ? getPlacementAt(prev, loc) : null;
            const entry = p ? getCatalogEntry(p.catalogId) : null;
            if (entry) {
                toast.success(
                    translate("simulator.toast.equipped", { name: itemName(entry.name, getLang()) }),
                );
            }
            return next;
        });
    }, [customizeWeaponId]);

    const onUnequipAttachmentSlot = useCallback(
        (attachSlot) => {
            if (!customizeWeaponId) return;
            setSim((prev) => {
                const weapon = prev.inv
                    .concat(prev.hotbar)
                    .find((p) => p?.instanceId === customizeWeaponId);
                const att = weapon?.attachments?.[attachSlot];
                const next = unequipAttachmentFromWeapon(prev, customizeWeaponId, attachSlot);
                if (!next) return prev;
                const e = att ? getCatalogEntry(att.catalogId) : null;
                if (e) {
                    toast.success(
                        translate("simulator.toast.unequipped", { name: itemName(e.name, getLang()) }),
                    );
                }
                return next;
            });
        },
        [customizeWeaponId],
    );

    const kitTotal = useMemo(() => computeKitTotal(sim), [sim]);
    const benefitsExtra = useMemo(
        () => computeBenefitsExtraCost(benefitState, budget),
        [benefitState, budget],
    );
    const total = useMemo(
        () => Math.round((kitTotal + benefitsExtra) * 10000) / 10000,
        [kitTotal, benefitsExtra],
    );

    // Só sobe o patamar automaticamente quando o kit cresce — não impede baixar manualmente (ex. simular R$ 40 com kit de R$ 60).
    useEffect(() => {
        const minB = minTierForAmount(total);
        setBudget((current) => (current < minB ? minB : current));
    }, [total]);

    useEffect(() => {
        const locked = budget < PREMIUM_MIN_BUDGET;
        setBenefitState((prev) => applyBudgetUnlocks(prev, budget, locked));
    }, [budget]);
    const vipName = name.trim();
    const hasVipName = vipName.length > 0;
    const meetsMinimumTotal = total >= MIN_VIP_INVOICE_TOTAL;
    const canDownloadInvoice = meetsMinimumTotal && hasVipName;
    const amountToMinimum = Math.max(0, MIN_VIP_INVOICE_TOTAL - total);
    const budgetBelowTotal = budget < total - 0.001;

    const summary = useMemo(() => {
        const c = (arr) => arr.filter(Boolean).length;
        const armorCount = Object.values(sim.armor).filter(Boolean).length;
        const benefits = buildBenefitsSummaryList(benefitState, budget, lang);
        return {
            inv: c(sim.inv),
            hot: c(sim.hotbar),
            armor: armorCount,
            benefits,
            benefitsExtra,
            items: buildInvoiceItems(sim, lang),
        };
    }, [sim, benefitState, budget, benefitsExtra, lang]);

    const downloadInvoice = useCallback(() => {
        if (!hasVipName) {
            toast.error(t("simulator.toast.nameRequired"));
            return;
        }
        if (!meetsMinimumTotal) {
            toast.error(t("simulator.toast.minTotal", { min: formatBRL(MIN_VIP_INVOICE_TOTAL, lang) }));
            return;
        }

        const benefits = buildBenefitsSummaryList(benefitState, budget, lang);
        const player = buildPlayerIdentityForInvoice(user, profile);

        const invoicePayload = {
            name: vipName,
            budget,
            benefits,
            items: summary.items,
            kitTotal,
            benefitsExtra,
            total,
            player,
        };

        try {
            downloadVipInvoice(invoicePayload, lang);
            setInvoiceSent(true);
            toast.success(t("simulator.toast.invoiceDownloaded"), {
                duration: 7000,
            });
        } catch {
            toast.error(t("simulator.toast.invoiceFailed"));
            return;
        }

        if (profile?.id && player.steamId) {
            /* Histórico na base de dados sempre em PT (equipa de admins), independentemente do idioma do site. */
            const historyInvoice =
                lang === "pt"
                    ? invoicePayload
                    : {
                          ...invoicePayload,
                          benefits: buildBenefitsSummaryList(benefitState, budget, "pt"),
                          items: buildInvoiceItems(sim, "pt"),
                      };
            saveVipSimulatorRequest({
                userId: profile.id,
                player,
                invoice: historyInvoice,
            }).then((res) => {
                if (!res.ok) {
                    toast.warning(translate("simulator.toast.historyFailed"));
                }
            });
        }

        // eslint-disable-next-line no-console
        console.log("[VIP simulador] pedido", {
            name: vipName,
            player,
            budget,
            benefits,
            items: summary.items,
            total,
        });
    }, [
        vipName,
        hasVipName,
        meetsMinimumTotal,
        budget,
        benefitState,
        summary.items,
        kitTotal,
        benefitsExtra,
        total,
        user,
        profile,
        sim,
        t,
        lang,
    ]);

    const renderSlotItem = (placement) => {
        if (!placement) return null;
        const entry = getCatalogEntry(placement.catalogId);
        if (!entry) return null;
        if (entry.cat === "weapon") {
            return (
                <WeaponSlotCard
                    placement={placement}
                    onRemoveFromKit={onRemoveFromKit}
                    onOpenCustomize={onOpenWeaponCustomize}
                />
            );
        }
        return (
            <KitItemCard placement={placement} entry={entry} onRemoveFromKit={onRemoveFromKit} />
        );
    };

    return (
        <div className="vip-sim-page">
            <div className="vip-sim-bg" aria-hidden />

            <div className="vip-sim-shell">
                    <header className="vip-sim-header">
                        <div className="vip-sim-header__left">
                            <Link className="vip-sim-back" to="/#vips">
                                {t("simulator.back")}
                            </Link>
                            <LanguageSwitch className="vip-sim-header__lang" />
                        </div>
                        <div className="vip-sim-header__brand">
                            <img className="vip-sim-header__logo" src={zrsLogo} alt="" width={40} height={40} />
                            <div className="vip-sim-header__brand-text">
                                <span className="vip-sim-header__mark">ZRS</span>
                                <span className="vip-sim-header__sub">{t("simulator.headerSub")}</span>
                            </div>
                        </div>
                    </header>

                    <section className="vip-sim-hero card-surface">
                        <h1 className="vip-sim-title">{t("simulator.title")}</h1>
                        <p className="vip-sim-lead">
                            {t("simulator.lead.p1")}
                            <strong>{t("simulator.lead.mods")}</strong>
                            {t("simulator.lead.or")}
                            <strong>{t("simulator.lead.dblClick")}</strong>
                            {t("simulator.lead.p2")}
                            <strong>×</strong>
                            {t("simulator.lead.p3")}
                            <strong>{t("simulator.lead.negotiable")}</strong>
                            {t("simulator.lead.end")}
                        </p>
                        <div className="vip-sim-min-notice" role="note">
                            <strong>
                                {t("simulator.minNotice.strong", { amount: formatBRL(MIN_VIP_INVOICE_TOTAL, lang) })}
                            </strong>
                            {t("simulator.minNotice.text")}
                        </div>
                        <div className="vip-sim-name-field">
                            <label className="vip-sim-label" htmlFor="vip-sim-name">
                                {t("simulator.nameLabel")}
                            </label>
                            <input
                                id="vip-sim-name"
                                className={`vip-sim-input ${!hasVipName && meetsMinimumTotal ? "vip-sim-input--required" : ""}`}
                                maxLength={48}
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                placeholder={t("simulator.namePlaceholder")}
                                autoComplete="off"
                                aria-required="true"
                                aria-invalid={!hasVipName}
                            />
                        </div>
                    </section>

                    <div className="vip-sim-body">
                        <div className="vip-sim-primary">
                            <div className="vip-sim-tabs" role="tablist" aria-label={t("simulator.tabs.aria")}>
                                <button
                                    type="button"
                                    className={`vip-sim-tab ${tab === "kit" ? "vip-sim-tab--active" : ""}`}
                                    role="tab"
                                    aria-selected={tab === "kit"}
                                    onClick={() => setTab("kit")}
                                >
                                    {t("simulator.tabs.kit")}
                                </button>
                                <button
                                    type="button"
                                    className={`vip-sim-tab ${tab === "beneficios" ? "vip-sim-tab--active" : ""}`}
                                    role="tab"
                                    aria-selected={tab === "beneficios"}
                                    onClick={() => setTab("beneficios")}
                                >
                                    {t("simulator.tabs.benefits")}
                                </button>
                            </div>

                            {tab === "kit" && (
                                <>
                                    <div className="vip-sim-workspace card-surface">
                                        <header className="vip-sim-workspace-head">
                                            <h2 className="vip-sim-panel-title">{t("simulator.workspace.title")}</h2>
                                            <p className="vip-sim-panel-lead">
                                                {t("simulator.workspace.lead1")}
                                                <strong>{t("simulator.lead.mods")}</strong>
                                                {t("simulator.workspace.lead2")}
                                            </p>
                                        </header>

                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleDragEnd}
                                        >
                                        <div className="vip-sim-kit-stage">
                                            <div className="vip-sim-kit-column">
                                                <section className="vip-sim-inv-block" aria-label={t("simulator.belt.aria")}>
                                                    <div className="vip-sim-subhead">
                                                        <h3 className="vip-sim-subhead__title">{t("simulator.belt.title")}</h3>
                                                        <span className="vip-sim-subhead__meta">{t("simulator.belt.meta")}</span>
                                                    </div>
                                                    <InventoryGrid
                                                        zone="hotbar"
                                                        columns={HOT_SLOTS}
                                                        count={HOT_SLOTS}
                                                        items={sim.hotbar}
                                                        renderSlotContent={(p) => renderSlotItem(p)}
                                                    />
                                                </section>
                                                <section className="vip-sim-inv-block" aria-label={t("simulator.inv.aria")}>
                                                    <div className="vip-sim-subhead">
                                                        <h3 className="vip-sim-subhead__title">{t("simulator.inv.title")}</h3>
                                                        <span className="vip-sim-subhead__meta">{t("simulator.inv.meta")}</span>
                                                    </div>
                                                    <InventoryGrid
                                                        zone="inv"
                                                        columns={INV_COLS}
                                                        count={INV_SLOTS}
                                                        items={sim.inv}
                                                        renderSlotContent={(p) => renderSlotItem(p)}
                                                    />
                                                </section>
                                            </div>
                                            <aside className="vip-sim-kit-equip" aria-label={t("simulator.equip.aria")}>
                                                <div className="vip-sim-subhead vip-sim-subhead--equip">
                                                    <h3 className="vip-sim-subhead__title">{t("simulator.equip.title")}</h3>
                                                    <span className="vip-sim-subhead__meta">{t("simulator.equip.meta")}</span>
                                                </div>
                                                <ArmorSlots
                                                    armor={sim.armor}
                                                    renderSlotContent={(p) => renderSlotItem(p)}
                                                    quickEquipBar={<QuickEquipBar onApply={onQuickEquip} />}
                                                />
                                            </aside>
                                        </div>
                                        </DndContext>
                                    </div>

                                    <div className="vip-sim-pool-outer card-surface">
                                        <AvailableItems
                                            pool={sim.pool}
                                            onQuantityChange={onQuantityChange}
                                            onAddToKit={onAddFromPool}
                                        />
                                    </div>
                                </>
                            )}

                            {tab === "beneficios" && (
                                <div className="vip-sim-benefits-wrap">
                                    <BenefitsPanel
                                        budget={budget}
                                        orderTotal={total}
                                        onBudgetChange={handleBudget}
                                        benefitState={benefitState}
                                        onBenefitPatch={onBenefitPatch}
                                        lockedPremium={lockedPremium}
                                    />
                                </div>
                            )}
                        </div>

                        <aside className="vip-sim-summary card-surface" aria-label={t("simulator.summary.aria")}>
                            <h2 className="vip-sim-summary__title">{t("simulator.summary.title")}</h2>

                            <div className="vip-sim-summary__grid">
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.vipName")}</span>
                                    <span className="vip-sim-summary__v">{name.trim() || "—"}</span>
                                </div>
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.budget")}</span>
                                    <span className="vip-sim-summary__v">R$ {budget}</span>
                                </div>
                                {budget > total + 0.001 ? (
                                    <p className="vip-sim-summary__budget-warn" role="status">
                                        {t("simulator.summary.budgetWarn")}
                                    </p>
                                ) : null}
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.inv")}</span>
                                    <span className="vip-sim-summary__v">
                                        {t("simulator.summary.itemsCount", { count: summary.inv })}
                                    </span>
                                </div>
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.belt")}</span>
                                    <span className="vip-sim-summary__v">
                                        {t("simulator.summary.itemsCount", { count: summary.hot })}
                                    </span>
                                </div>
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.gear")}</span>
                                    <span className="vip-sim-summary__v">
                                        {t("simulator.summary.piecesCount", { count: summary.armor })}
                                    </span>
                                </div>
                            </div>

                            <div className="vip-sim-summary__block">
                                <span className="vip-sim-summary__k">{t("simulator.summary.invoiceItems")}</span>
                                {summary.items.length ? (
                                    <div className="vip-sim-summary__invoice">
                                        <div className="vip-sim-summary__invoice-row vip-sim-summary__invoice-row--head">
                                            <span>{t("simulator.summary.colName")}</span>
                                            <span>{t("simulator.summary.colQty")}</span>
                                            <span>{t("simulator.summary.colUnit")}</span>
                                            <span>{t("simulator.summary.colSub")}</span>
                                        </div>
                                        {summary.items.map((row) => (
                                            <div key={row.key} className="vip-sim-summary__invoice-row">
                                                <span className="vip-sim-summary__invoice-name">{row.name}</span>
                                                <span className="vip-sim-summary__invoice-qty">
                                                    x{formatQty(row.quantity, lang)}
                                                </span>
                                                <span className="vip-sim-summary__invoice-price">
                                                    {formatUnitBRL(row.unitPrice, lang)}
                                                </span>
                                                <span className="vip-sim-summary__invoice-sub">
                                                    {formatBRL(row.subtotal, lang)}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                ) : (
                                    <span className="vip-sim-summary__v vip-sim-summary__v--muted">
                                        {t("simulator.summary.noItems")}
                                    </span>
                                )}
                            </div>

                            <div className="vip-sim-summary__block">
                                <span className="vip-sim-summary__k">{t("simulator.summary.benefits")}</span>
                                {summary.benefits.length ? (
                                    <ul className="vip-sim-summary__list">
                                        {summary.benefits.map((b) => (
                                            <li key={b.id}>{b.text}</li>
                                        ))}
                                    </ul>
                                ) : (
                                    <span className="vip-sim-summary__v vip-sim-summary__v--muted">{t("simulator.summary.none")}</span>
                                )}
                            </div>

                            <div className="vip-sim-summary__divider" />

                            {summary.benefitsExtra > 0 ? (
                                <div className="vip-sim-summary__row">
                                    <span className="vip-sim-summary__k">{t("simulator.summary.extras")}</span>
                                    <span className="vip-sim-summary__v">{formatBRL(summary.benefitsExtra, lang)}</span>
                                </div>
                            ) : null}

                            <div
                                className={`vip-sim-summary__total ${!meetsMinimumTotal ? "vip-sim-summary__total--below-min" : ""}`}
                            >
                                <span className="vip-sim-summary__total-label">{t("simulator.summary.total")}</span>
                                <span className="vip-sim-summary__total-num">{formatBRL(total, lang)}</span>
                                {summary.benefitsExtra > 0 ? (
                                    <span className="vip-sim-summary__total-sub">
                                        {t("simulator.summary.kitPlusBenefits", {
                                            kit: formatBRL(kitTotal, lang),
                                            extras: formatBRL(summary.benefitsExtra, lang),
                                        })}
                                    </span>
                                ) : null}
                                <span className="vip-sim-summary__total-sub">
                                    {t("simulator.summary.budgetLine", { budget })}
                                    {budgetBelowTotal ? t("simulator.summary.budgetBelow") : ""}
                                </span>
                                {!meetsMinimumTotal ? (
                                    <span className="vip-sim-summary__total-warn">
                                        {t("simulator.summary.missingMin", {
                                            amount: formatBRL(amountToMinimum, lang),
                                            min: formatBRL(MIN_VIP_INVOICE_TOTAL, lang),
                                        })}
                                    </span>
                                ) : null}
                            </div>
                            <p className="vip-sim-summary__final">
                                {t("simulator.summary.finalPrice")}
                                <em>{t("simulator.summary.negotiable")}</em>
                            </p>

                            <div className="vip-sim-submit-block">
                                <button
                                    type="button"
                                    className="vip-sim-btn-primary"
                                    onClick={downloadInvoice}
                                    disabled={!canDownloadInvoice}
                                    aria-disabled={!canDownloadInvoice}
                                >
                                    {t("simulator.summary.download")}
                                </button>
                                {!canDownloadInvoice ? (
                                    <p className="vip-sim-submit-min" role="alert">
                                        {t("simulator.summary.toDownload")}
                                        <ul className="vip-sim-submit-min__list">
                                            {!hasVipName ? (
                                                <li>
                                                    {t("simulator.summary.needName1")}
                                                    <strong>{t("simulator.summary.needName2")}</strong>
                                                    {t("simulator.summary.needName3")}
                                                </li>
                                            ) : null}
                                            {!meetsMinimumTotal ? (
                                                <li>
                                                    {t("simulator.summary.needMin1")}
                                                    <strong>{formatBRL(MIN_VIP_INVOICE_TOTAL, lang)}</strong>
                                                    {t("simulator.summary.needMin2", {
                                                        amount: formatBRL(amountToMinimum, lang),
                                                    })}
                                                </li>
                                            ) : null}
                                        </ul>
                                        {meetsMinimumTotal && !hasVipName ? (
                                            <span className="vip-sim-submit-min__note">
                                                {t("simulator.summary.tierNote", { budget })}
                                            </span>
                                        ) : null}
                                    </p>
                                ) : (
                                    <p className="vip-sim-submit-hint">
                                        {t("simulator.summary.hint1")}
                                        <strong>{t("simulator.summary.hintInvoice")}</strong>
                                        {t("simulator.summary.hint2")}
                                        <strong>{t("simulator.summary.hintVips")}</strong>
                                        {t("simulator.summary.hint3")}
                                    </p>
                                )}
                                {invoiceSent && canDownloadInvoice ? (
                                    <div className="vip-sim-submit-success" role="status">
                                        <span className="vip-sim-submit-success__icon" aria-hidden>
                                            ✓
                                        </span>
                                        <p>{t("simulator.summary.success")}</p>
                                    </div>
                                ) : null}
                                {canDownloadInvoice ? (
                                    <VipDiscordTicketButton highlight={invoiceSent} />
                                ) : null}
                            </div>
                        </aside>
                    </div>
                </div>

            <WeaponCustomizeModal
                open={Boolean(customizeWeaponId)}
                weaponInstanceId={customizeWeaponId}
                sim={sim}
                onClose={onCloseWeaponCustomize}
                onEquipFromPool={onEquipAttachmentFromPool}
                onUnequipSlot={onUnequipAttachmentSlot}
            />
        </div>
    );
}
